
import { createCheckboxTokens } from '../tokens/components.js';
import { type SemanticTokens, SEMANTIC } from '../tokens/semantic.js';

/**
 * Checkbox Component Configuration
 * 
 * Uses icon-based approach - hides default PrimeNG checkbox and uses primeicons
 * All variant colors from checkboxTokens in tokens/components.ts
 */
export function createCheckbox(s: SemanticTokens) {
  const checkboxTokens = createCheckboxTokens(s);
  return {
  root: {
    borderRadius: checkboxTokens.base.radius,
    width: checkboxTokens.base.size,
    height: checkboxTokens.base.size,
  },
  icon: {
    size: checkboxTokens.base.iconSize,
    color: checkboxTokens.secondary.checked,
    checkedColor: checkboxTokens.secondary.checked,
    checkedHoverColor: checkboxTokens.secondary.checkedHover,
    disabledColor: checkboxTokens.disabled,
  },
  css: () => `
    .p-checkbox {
      scale: ${checkboxTokens.base.scale};
      width: ${checkboxTokens.base.size};
      height: ${checkboxTokens.base.size};
      position: relative;
    }
    
    .p-checkbox .p-checkbox-box {
      width: ${checkboxTokens.base.size};
      height: ${checkboxTokens.base.size};
      border: none;
      background: transparent;
      border-radius: 0;
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all ${checkboxTokens.base.transition};
    }

    .p-checkbox.p-disabled .p-checkbox-box,
    .p-checkbox-checked:not(.p-disabled):has(.p-checkbox-input:hover) .p-checkbox-box {
      background: transparent;
    }

    /* Hide default PrimeNG check icon - using :root for specificity */
    :root .p-checkbox .p-checkbox-box .p-checkbox-icon,
    :root .p-checkbox .p-checkbox-box svg,
    :root .p-checkbox .p-checkbox-box .p-icon,
    :root .p-checkbox .p-checkbox-box checkicon {
      display: none !important;
      opacity: 0;
      visibility: hidden;
    }
    
    /* Unchecked icon */
    :root .p-checkbox .p-checkbox-box::before {
      content: "\\e9d1";
      font-family: 'primeicons';
      font-size: ${checkboxTokens.base.iconSize};
      line-height: 1;
      color: ${checkboxTokens.secondary.checked};
      display: block;
      transition: all ${checkboxTokens.base.transition};
    }

    /* Checked icon */
    .p-checkbox.p-checkbox-checked .p-checkbox-box::before,
    .p-checkbox.p-highlight .p-checkbox-box::before,
    .p-checkbox[data-p-checked="true"] .p-checkbox-box::before {
      content: "\\e98c";
      color: ${checkboxTokens.secondary.checked};
    }
    
    /* Hover state - unchecked */
    .p-checkbox:not(.p-disabled):hover .p-checkbox-box::before {
      color: ${checkboxTokens.secondary.hover};
    }
    
    /* Hover state - checked */
    .p-checkbox.p-checkbox-checked:not(.p-disabled):hover .p-checkbox-box::before,
    .p-checkbox.p-highlight:not(.p-disabled):hover .p-checkbox-box::before,
    .p-checkbox[data-p-checked="true"]:not(.p-disabled):hover .p-checkbox-box::before {
      color: ${checkboxTokens.secondary.checkedHover};
    }

    /* Primary variant */
    .p-checkbox.p-checkbox-primary .p-checkbox-box::before { 
      color: ${checkboxTokens.primary.unchecked};
    }
    .p-checkbox.p-checkbox-primary.p-checkbox-checked .p-checkbox-box::before,
    .p-checkbox.p-checkbox-primary.p-highlight .p-checkbox-box::before,
    .p-checkbox.p-checkbox-primary[data-p-checked="true"] .p-checkbox-box::before {
      color: ${checkboxTokens.primary.checked};
    }
    .p-checkbox.p-checkbox-primary:not(.p-disabled):hover .p-checkbox-box::before {
      color: ${checkboxTokens.primary.hover};
    }
    .p-checkbox.p-checkbox-primary.p-checkbox-checked:not(.p-disabled):hover .p-checkbox-box::before,
    .p-checkbox.p-checkbox-primary.p-highlight:not(.p-disabled):hover .p-checkbox-box::before,
    .p-checkbox.p-checkbox-primary[data-p-checked="true"]:not(.p-disabled):hover .p-checkbox-box::before {
      color: ${checkboxTokens.primary.checkedHover};
    }
    .p-checkbox.p-checkbox-primary .p-checkbox-box:focus { 
      box-shadow: 0 0 0 2px ${checkboxTokens.primary.focus}; 
    }

    /* Secondary variant (default) */
    .p-checkbox.p-checkbox-secondary .p-checkbox-box::before { 
      color: ${checkboxTokens.secondary.unchecked};
    }
    .p-checkbox.p-checkbox-secondary.p-checkbox-checked .p-checkbox-box::before,
    .p-checkbox.p-checkbox-secondary.p-highlight .p-checkbox-box::before,
    .p-checkbox.p-checkbox-secondary[data-p-checked="true"] .p-checkbox-box::before {
      color: ${checkboxTokens.secondary.checked};
    }
    .p-checkbox.p-checkbox-secondary:not(.p-disabled):hover .p-checkbox-box::before {
      color: ${checkboxTokens.secondary.hover};
    }
    .p-checkbox.p-checkbox-secondary.p-checkbox-checked:not(.p-disabled):hover .p-checkbox-box::before,
    .p-checkbox.p-checkbox-secondary.p-highlight:not(.p-disabled):hover .p-checkbox-box::before,
    .p-checkbox.p-checkbox-secondary[data-p-checked="true"]:not(.p-disabled):hover .p-checkbox-box::before {
      color: ${checkboxTokens.secondary.checkedHover};
    }
    .p-checkbox.p-checkbox-secondary .p-checkbox-box:focus { 
      box-shadow: 0 0 0 2px ${checkboxTokens.secondary.focus}; 
    }

    /* Surface variant - uses neutral palette */
    .p-checkbox.p-checkbox-surface .p-checkbox-box::before { 
      color: ${checkboxTokens.surfaceVariant.unchecked};
    }
    .p-checkbox.p-checkbox-surface.p-checkbox-checked .p-checkbox-box::before,
    .p-checkbox.p-checkbox-surface.p-highlight .p-checkbox-box::before,
    .p-checkbox.p-checkbox-surface[data-p-checked="true"] .p-checkbox-box::before {
      color: ${checkboxTokens.surfaceVariant.checked};
    }
    .p-checkbox.p-checkbox-surface:not(.p-disabled):hover .p-checkbox-box::before {
      color: ${checkboxTokens.surfaceVariant.hover};
    }
    .p-checkbox.p-checkbox-surface.p-checkbox-checked:not(.p-disabled):hover .p-checkbox-box::before,
    .p-checkbox.p-checkbox-surface.p-highlight:not(.p-disabled):hover .p-checkbox-box::before,
    .p-checkbox.p-checkbox-surface[data-p-checked="true"]:not(.p-disabled):hover .p-checkbox-box::before {
      color: ${checkboxTokens.surfaceVariant.checkedHover};
    }
    .p-checkbox.p-checkbox-surface .p-checkbox-box:focus { 
      box-shadow: 0 0 0 2px ${checkboxTokens.surfaceVariant.focus}; 
    }

    /* Success variant */
    .p-checkbox.p-checkbox-success .p-checkbox-box::before { 
      color: ${checkboxTokens.success.unchecked};
    }
    .p-checkbox.p-checkbox-success.p-checkbox-checked .p-checkbox-box::before,
    .p-checkbox.p-checkbox-success.p-highlight .p-checkbox-box::before,
    .p-checkbox.p-checkbox-success[data-p-checked="true"] .p-checkbox-box::before {
      color: ${checkboxTokens.success.checked};
    }
    .p-checkbox.p-checkbox-success:not(.p-disabled):hover .p-checkbox-box::before {
      color: ${checkboxTokens.success.hover};
    }
    .p-checkbox.p-checkbox-success.p-checkbox-checked:not(.p-disabled):hover .p-checkbox-box::before,
    .p-checkbox.p-checkbox-success.p-highlight:not(.p-disabled):hover .p-checkbox-box::before,
    .p-checkbox.p-checkbox-success[data-p-checked="true"]:not(.p-disabled):hover .p-checkbox-box::before {
      color: ${checkboxTokens.success.checkedHover};
    }
    .p-checkbox.p-checkbox-success .p-checkbox-box:focus { 
      box-shadow: 0 0 0 2px ${checkboxTokens.success.focus}; 
    }

    /* Danger variant */
    .p-checkbox.p-checkbox-danger .p-checkbox-box::before { 
      color: ${checkboxTokens.danger.unchecked};
    }
    .p-checkbox.p-checkbox-danger.p-checkbox-checked .p-checkbox-box::before,
    .p-checkbox.p-checkbox-danger.p-highlight .p-checkbox-box::before,
    .p-checkbox.p-checkbox-danger[data-p-checked="true"] .p-checkbox-box::before {
      color: ${checkboxTokens.danger.checked};
    }
    .p-checkbox.p-checkbox-danger:not(.p-disabled):hover .p-checkbox-box::before {
      color: ${checkboxTokens.danger.hover};
    }
    .p-checkbox.p-checkbox-danger.p-checkbox-checked:not(.p-disabled):hover .p-checkbox-box::before,
    .p-checkbox.p-checkbox-danger.p-highlight:not(.p-disabled):hover .p-checkbox-box::before,
    .p-checkbox.p-checkbox-danger[data-p-checked="true"]:not(.p-disabled):hover .p-checkbox-box::before {
      color: ${checkboxTokens.danger.checkedHover};
    }
    .p-checkbox.p-checkbox-danger .p-checkbox-box:focus { 
      box-shadow: 0 0 0 2px ${checkboxTokens.danger.focus}; 
    }

    /* Warning variant */
    .p-checkbox.p-checkbox-warning .p-checkbox-box::before { 
      color: ${checkboxTokens.warning.unchecked};
    }
    .p-checkbox.p-checkbox-warning.p-checkbox-checked .p-checkbox-box::before,
    .p-checkbox.p-checkbox-warning.p-highlight .p-checkbox-box::before,
    .p-checkbox.p-checkbox-warning[data-p-checked="true"] .p-checkbox-box::before {
      color: ${checkboxTokens.warning.checked};
    }
    .p-checkbox.p-checkbox-warning:not(.p-disabled):hover .p-checkbox-box::before {
      color: ${checkboxTokens.warning.hover};
    }
    .p-checkbox.p-checkbox-warning.p-checkbox-checked:not(.p-disabled):hover .p-checkbox-box::before,
    .p-checkbox.p-checkbox-warning.p-highlight:not(.p-disabled):hover .p-checkbox-box::before,
    .p-checkbox.p-checkbox-warning[data-p-checked="true"]:not(.p-disabled):hover .p-checkbox-box::before {
      color: ${checkboxTokens.warning.checkedHover};
    }
    .p-checkbox.p-checkbox-warning .p-checkbox-box:focus { 
      box-shadow: 0 0 0 2px ${checkboxTokens.warning.focus}; 
    }
    
    /* Disabled state */
    .p-checkbox.p-disabled .p-checkbox-box,
    .p-checkbox-disabled .p-checkbox-box {
      color: ${checkboxTokens.disabled};
    }
    
    .p-checkbox.p-disabled .p-checkbox-box::before,
    .p-checkbox-disabled .p-checkbox-box::before {
      color: ${checkboxTokens.disabled};
    }
    
    /* Invalid state */
    .p-checkbox.p-invalid .p-checkbox-box { 
      border-color: ${checkboxTokens.invalid}; 
    }
    .p-checkbox.p-invalid .p-checkbox-box::before { 
      color: ${checkboxTokens.invalid}; 
    }
    .p-checkbox.p-invalid:not(.p-disabled):hover .p-checkbox-box::before { 
      color: ${checkboxTokens.invalid}; 
    }
    
    /* Focus ring */
    .p-checkbox .p-checkbox-box:focus {
      outline: none;
      box-shadow: 0 0 0 2px ${checkboxTokens.secondary.focus};
    }

    /* Simple variant (no background change) */
    .simple .p-checkbox.p-checkbox-checked .p-checkbox-box::before,
    .simple .p-checkbox.p-highlight .p-checkbox-box::before,
    .simple .p-checkbox[data-p-checked="true"] .p-checkbox-box::before {
      color: ${checkboxTokens.secondary.checked};
    }

    /* Active variant (use checked color) */
    .active .p-checkbox.p-checkbox-checked .p-checkbox-box::before,
    .active .p-checkbox.p-highlight .p-checkbox-box::before,
    .active .p-checkbox[data-p-checked="true"] .p-checkbox-box::before {
      color: ${checkboxTokens.secondary.checked};
    }
  `,
  };
}
export const checkbox = createCheckbox(SEMANTIC);